"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TherapistListOpenItemSchema = exports.TherapistListItemSchema = exports.TherapistListFilterSchema = exports.TherapistListFilterSessionSchema = exports.TherapistListFilterOfficeDistrictsSchema = exports.TherapistListFilterAvailableSchema = exports.TherapistListFilterAvailableAtTimesSchema = exports.TherapistListFilterAvailableInDaysCountSchema = exports.TherapistDetailPrivateSchema = exports.TherapistDetailPublicSchema = exports.TherapistDetailSchema = exports.TherapistBillingInfoSchema = exports.TherapistBaseSchema = exports.BookingWindowHoursSchema = void 0;
const zod_1 = require("zod");
const therapistOffice_1 = require("./therapistOffice");
const common_1 = require("../../common");
const THERAPIST_1 = require("../../../enums/THERAPIST");
const SESSION_1 = require("../../../enums/SESSION");
// Later we can be strict and use this for BE & FE validation with like .min(4).max(24)
exports.BookingWindowHoursSchema = zod_1.z.number().int().nonnegative().min(0);
// TODO: Make this a _pick_ from various base entities.
// Most of the fields are nullable because our DB is hell 🔥🔥🔥
exports.TherapistBaseSchema = zod_1.z.object({
    therapistUserId: common_1.NumericIdSchema,
    therapistId: common_1.NumericIdSchema,
    // AppUsers table
    firstName: zod_1.z.string().nullable(),
    lastName: zod_1.z.string().nullable(),
    slug: zod_1.z.string().nullable(),
    // Therapist table
    experience: zod_1.z.number(),
    image: zod_1.z.string().nullable(),
    videoId: zod_1.z.string().nullable(),
    description: zod_1.z.string().nullable(),
    // in admin this is called verificationStatus and is an enum
    verifiedCertificate: zod_1.z.number().nullable(),
    titleBefore: zod_1.z.string().nullable(),
    titleAfter: zod_1.z.string().nullable(),
    bookingWindowHours: exports.BookingWindowHoursSchema,
    hasOfflineTherapy: zod_1.z.boolean(),
    sex: zod_1.z.number().nullable(),
    status: zod_1.z.nativeEnum(THERAPIST_1.THERAPIST_STATUS),
    therapistCategoryId: common_1.NumericIdSchema.nullable(),
    services: zod_1.z.array(common_1.NumericIdSchema),
    visible: zod_1.z.boolean(),
    // Relations
    majorSpecializations: zod_1.z.array(common_1.NumericIdSchema),
    minorSpecializations: zod_1.z.array(common_1.NumericIdSchema),
    insurances: zod_1.z.array(common_1.NumericIdSchema),
    trainings: zod_1.z.array(common_1.NumericIdSchema).default([]),
    languages: zod_1.z.array(zod_1.z.string()),
    // Computed data
    freeSchedulesCountLongTerm: common_1.ItemCountSchema,
});
exports.TherapistBillingInfoSchema = zod_1.z.object({
    vatin: zod_1.z.string().nullable(),
    cin: zod_1.z.string().nullable(),
    houseNumber: zod_1.z.string().nullable(),
    street: zod_1.z.string().nullable(),
    zip: zod_1.z.string().nullable(),
    city: zod_1.z.string().nullable(),
    country: zod_1.z.string().nullable(),
});
const TherapistDetailOfficeSchema = therapistOffice_1.TherapistOfficeSchema.extend({
    clinicId: common_1.NumericIdSchema.nullable().openapi({
        description: 'set when the therapist works at a clinic; the office is then the clinic',
    }),
});
exports.TherapistDetailSchema = zod_1.z.object({
    ...exports.TherapistBaseSchema.shape,
    // Different in detail and list
    recommendations: common_1.ItemCountSchema.nullable(),
    office: TherapistDetailOfficeSchema.partial({ phoneNumber: true, additionalInformation: true })
        .openapi({
        description: 'phoneNumber and additionalInformation is included only when the user has an upcoming reserved offline event with the therapist; or is the therapist themselves',
    })
        .nullable(),
    // Extra fields
    education: zod_1.z.string().nullable(),
    certificates: zod_1.z.string().nullable(),
    token: zod_1.z.string().optional().openapi({
        description: 'calendar token for the therapist; sent only when authenticated and the therapist is requesting their own detail',
    }),
    aiTranscriptEnabled: zod_1.z.boolean().optional().openapi({
        description: 'whether the therapist has Hedepy AI transcript enabled; sent only when authenticated and the therapist is requesting their own detail',
    }),
    billingInfo: exports.TherapistBillingInfoSchema.nullable(),
});
exports.TherapistDetailPublicSchema = zod_1.z.object({
    ...exports.TherapistBaseSchema.shape,
    // Different in detail and list
    recommendations: common_1.ItemCountSchema,
    office: TherapistDetailOfficeSchema.partial({ phoneNumber: true, additionalInformation: true })
        .openapi({
        description: 'phoneNumber and additionalInformation is included only when the user has an upcoming reserved offline event with the therapist; or is the therapist themselves',
    })
        .nullable(),
    // Extra fields
    education: zod_1.z.string().nullable(),
    certificates: zod_1.z.string().nullable(),
    billingInfo: exports.TherapistBillingInfoSchema.nullable(),
});
exports.TherapistDetailPrivateSchema = zod_1.z.object({
    ...exports.TherapistDetailPublicSchema.shape,
    office: TherapistDetailOfficeSchema.nullable(),
    aiTranscriptEnabled: zod_1.z.boolean(),
    // This is AppUsers.token, but renamed
    // - to be explicit
    // - so we do not try to use it for anything else (that would be fucking stupid)
    // If we ever do anything with calendar integrations (keeping it still in our platform),
    // we should get rid of AppUsers.token, because it's ambiguous and poorly designed.
    calendarToken: zod_1.z.string().nullable(),
});
// List schemas
exports.TherapistListFilterAvailableInDaysCountSchema = zod_1.z.object({
    availableInDaysCount: zod_1.z.array(zod_1.z.number().int().min(0).max(3)).openapi({
        description: 'therapist available in the next given days count (0 is today, 1 is tomorrow, etc.)',
    }),
});
exports.TherapistListFilterAvailableAtTimesSchema = zod_1.z.object({
    days: zod_1.z.array(zod_1.z.number().min(0).max(6)),
    times: zod_1.z.array(zod_1.z.string().regex(/^\d{1,2}-\d{1,2}$/)),
});
exports.TherapistListFilterAvailableSchema = zod_1.z.discriminatedUnion('type', [
    zod_1.z.object({ type: zod_1.z.literal('atTimes'), ...exports.TherapistListFilterAvailableAtTimesSchema.shape }),
    zod_1.z.object({ type: zod_1.z.literal('inDaysCount'), ...exports.TherapistListFilterAvailableInDaysCountSchema.shape }),
]);
exports.TherapistListFilterOfficeDistrictsSchema = zod_1.z.array(common_1.NumericIdSchema);
const HasOpenSlotsSchema = zod_1.z.literal(true).optional().openapi({
    description: 'when true, keep only therapists with at least one bookable slot. With a session filter the slot must match its type (online → online/hybrid slots; offline & hybrid → offline/hybrid slots); without a session filter, any slot type counts.',
});
exports.TherapistListFilterSessionSchema = zod_1.z.union([
    zod_1.z.object({
        type: zod_1.z.union([zod_1.z.literal(SESSION_1.SESSION_TYPE.OFFLINE), zod_1.z.literal(SESSION_1.SESSION_TYPE.HYBRID)]),
        officeDistrictIds: exports.TherapistListFilterOfficeDistrictsSchema.optional(),
    }),
    zod_1.z.object({
        type: zod_1.z.literal(SESSION_1.SESSION_TYPE.ONLINE),
    }),
]);
exports.TherapistListFilterSchema = zod_1.z.object({
    serviceId: common_1.NumericIdSchema,
    available: exports.TherapistListFilterAvailableSchema.optional(),
    session: exports.TherapistListFilterSessionSchema.optional(),
    hasOpenSlots: HasOpenSlotsSchema,
});
const TherapistPriceItemSchema = zod_1.z.object({
    price: zod_1.z.number(),
    serviceId: common_1.NumericIdSchema,
});
const TherapistListOfficeLocationSchema = therapistOffice_1.TherapistOfficeLocationSchema.extend({
    districtName: zod_1.z.string().nullable(),
});
// Base schema for therapist list items (used by both list and list-open)
const TherapistListItemBaseSchema = zod_1.z.object({
    ...exports.TherapistBaseSchema.shape,
    // Different in detail and list
    recommendations: common_1.ItemCountSchema,
    office: therapistOffice_1.TherapistOfficeSchema.omit({ phoneNumber: true, additionalInformation: true })
        .extend({ location: TherapistListOfficeLocationSchema })
        .nullable(),
    // Extra fields
    prices: zod_1.z.array(TherapistPriceItemSchema),
    pricesCurrency: zod_1.z.string(),
    freeSchedulesCount: common_1.ItemCountSchema,
});
exports.TherapistListItemSchema = TherapistListItemBaseSchema;
exports.TherapistListOpenItemSchema = TherapistListItemBaseSchema.extend({
    // These two take 100s of kB in CZ and are used only in therapist detail
    education: zod_1.z.string().nullable(),
    certificates: zod_1.z.string().nullable(),
});
